import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, MapPin, Scan, CheckCircle, AlertCircle, RotateCcw, Box, RefreshCw, Clock, CheckCircle2, Circle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const MobileFGPutAway = () => {
  const navigate = useNavigate();
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const packInputRef = useRef(null);
  const binInputRef = useRef(null);

  // States
  const [step, setStep] = useState('staff'); // staff, bundle, progress-view, pack, success
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffList, setStaffList] = useState([]); // [{staff_id, staff_name}] multi-staff team
  const [bundleCode, setBundleCode] = useState('');
  const [bundleDetails, setBundleDetails] = useState(null);
  const [packBarcode, setPackBarcode] = useState('');
  const [suggestedBin, setSuggestedBin] = useState(null);
  const [binCode, setBinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);
  
  // Progress view state (for session recovery)
  const [progressData, setProgressData] = useState(null);
  const [activeSlipId, setActiveSlipId] = useState(null);

  useEffect(() => {
    if (step === 'staff') {
      setTimeout(() => staffInputRef.current?.focus(), 100);
    } else if (step === 'bundle') {
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } else if (step === 'pack') {
      setTimeout(() => packInputRef.current?.focus(), 100);
    }
  }, [step]);

  const vibrate = (pattern = [50]) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  // Fetch progress data for a slip
  const fetchSlipProgress = async (slipId) => {
    try {
      const response = await apiClient.get(`/wms/fg/put-away/slip-progress/${slipId}`);
      setProgressData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching slip progress:', error);
      return null;
    }
  };
  
  // Continue from progress view
  const continueFromProgress = () => {
    setStep('pack');
    setProgressData(null);
  };
  
  // Refresh progress data
  const refreshProgress = async () => {
    if (activeSlipId) {
      setLoading(true);
      await fetchSlipProgress(activeSlipId);
      setLoading(false);
    }
  };

  const handleStaffValidate = async () => {
    const id = staffId.trim();
    if (!id) {
      toast.error('Please scan Staff ID');
      vibrate([100, 50, 100]);
      return;
    }
    if (staffList.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffId('');
      setTimeout(() => staffInputRef.current?.focus(), 100);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.get(`/validate/staff/${id}`);
      if (response.data.valid) {
        const nm = response.data.staff_name;
        setStaffList((prev) => [...prev, { staff_id: id, staff_name: nm }]);
        setStaffId('');
        vibrate([50]);
        toast.success(`Added ${nm}`);
        setTimeout(() => staffInputRef.current?.focus(), 100);
      } else {
        toast.error('Invalid Staff ID');
        vibrate([100, 50, 100]);
      }
    } catch (error) {
      toast.error('Staff validation failed');
      vibrate([100, 50, 100]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveStaff = (id) => {
    setStaffList((prev) => prev.filter((s) => s.staff_id !== id));
  };

  const handleStartSession = () => {
    if (staffList.length === 0) {
      toast.error('Add at least one staff');
      vibrate([100, 50, 100]);
      return;
    }
    setStaffName(staffList.map((s) => s.staff_name).join(', '));
    setStep('bundle');
    vibrate([50]);
  };

  const handleBundleScan = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan bundle barcode');
      vibrate([100, 50, 100]);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post(`/wms/fg/validate-bundle/${bundleCode}`);
      
      setBundleDetails(response.data.bundle);
      
      // Check if there's an active slip for this bundle that's in_progress
      if (response.data.bundle.slip_id && response.data.bundle.status === 'in_progress') {
        setActiveSlipId(response.data.bundle.slip_id);
        const progress = await fetchSlipProgress(response.data.bundle.slip_id);
        
        if (progress && (progress.completed_usns.length > 0 || progress.pending_usns.length > 0)) {
          // Show progress view before continuing
          setStep('progress-view');
          vibrate([50]);
          toast.info('Resuming previous session');
          return;
        }
      }
      
      // Get bin suggestion
      const binResponse = await apiClient.post(`/wms/fg/suggest-bin/${response.data.bundle.sku_code}`);
      if (binResponse.data.suggested_bin) {
        setSuggestedBin(binResponse.data.suggested_bin);
      }
      
      // Keep bin location input blank - user must manually enter/scan
      setBinCode('');
      
      setStep('pack');
      vibrate([50]);
      toast.success(`Bundle validated! ${response.data.bundle.remaining_packs} packs to put away.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bundle not found or not in IN_WIP');
      vibrate([100, 50, 100]);
      setBundleCode('');
    } finally {
      setLoading(false);
    }
  };

  const handlePackPutAway = async () => {
    if (!packBarcode.trim()) {
      toast.error('Please scan pack barcode');
      vibrate([100, 50, 100]);
      return;
    }
    if (!binCode.trim()) {
      toast.error('Please enter bin location');
      vibrate([100, 50, 100]);
      return;
    }

    try {
      setLoading(true);
      const response = await apiClient.post('/wms/fg/put-away-pack', {
        staff_id: staffList[0]?.staff_id || '',
        staff_ids: staffList.map((s) => s.staff_id),
        staff_index: sessionCount,
        pack_barcode: packBarcode,
        bundle_code: bundleCode,
        bin_code: binCode
      });

      vibrate([50, 50, 50]);
      toast.success('Pack stored!');
      setSessionCount(prev => prev + 1);
      
      // Update bundle details
      setBundleDetails(prev => ({
        ...prev,
        putaway_count: response.data.progress.putaway_count,
        remaining_packs: prev.total_packs - response.data.progress.putaway_count
      }));
      
      setPackBarcode('');
      
      if (response.data.progress.bundle_complete) {
        setStep('success');
        setTimeout(() => {
          resetForNextBundle();
        }, 2500);
      } else {
        packInputRef.current?.focus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Put-away failed');
      vibrate([100, 50, 100]);
      setPackBarcode('');
      packInputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const resetForNextBundle = () => {
    setBundleCode('');
    setBundleDetails(null);
    setPackBarcode('');
    setSuggestedBin(null);
    setBinCode('');
    setStep('bundle');
  };

  const resetAll = () => {
    setStaffId('');
    setStaffName('');
    setStaffList([]);
    setBundleCode('');
    setBundleDetails(null);
    setPackBarcode('');
    setSuggestedBin(null);
    setBinCode('');
    setSessionCount(0);
    setStep('staff');
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const progressPercentage = bundleDetails 
    ? Math.round((bundleDetails.putaway_count / bundleDetails.total_packs) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 to-teal-800 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/wms/mobile')}
            className="text-white hover:bg-white/20 -ml-2"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="text-center">
            <h1 className="text-xl font-bold">FG Put-Away</h1>
            {staffName && (
              <p className="text-sm text-teal-200">{staffName}</p>
            )}
          </div>
          <div className="w-10" />
        </div>
        
        {/* Bundle Progress */}
        {bundleDetails && step === 'pack' && (
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>{bundleDetails.putaway_count} of {bundleDetails.total_packs} packs</span>
              <span>{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-3 bg-teal-900" />
          </div>
        )}
      </header>

      {/* Session Stats */}
      {step !== 'staff' && (
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between text-sm">
          <span className="text-gray-400">Session Packs:</span>
          <span className="font-bold text-green-400">{sessionCount}</span>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 p-4 flex flex-col">
        {/* Step: Staff Validation (multi-staff) */}
        {step === 'staff' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-5">
              <div className="w-20 h-20 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="w-10 h-10 text-teal-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan Staff ID</h2>
                <p className="text-gray-400">Add one or more staff sharing this put-away</p>
              </div>
              <Input
                ref={staffInputRef}
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleStaffValidate)}
                placeholder="Staff ID"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl"
                data-testid="mobile-fg-staff-input"
              />
              <Button
                onClick={handleStaffValidate}
                disabled={loading || !staffId}
                className="w-full h-14 text-lg font-bold bg-teal-600 hover:bg-teal-700 rounded-xl"
                data-testid="mobile-fg-staff-add-btn"
              >
                {loading ? 'Validating...' : '+ Add Staff'}
              </Button>

              {staffList.length > 0 && (
                <div className="space-y-2" data-testid="mobile-fg-staff-list">
                  <p className="text-xs text-gray-400 font-medium">{staffList.length} staff added:</p>
                  <div className="flex flex-wrap gap-2">
                    {staffList.map((s) => (
                      <div
                        key={s.staff_id}
                        className="flex items-center gap-2 bg-teal-500/20 border border-teal-500/40 rounded-full pl-3 pr-1 py-1"
                        data-testid={`mobile-fg-staff-chip-${s.staff_id}`}
                      >
                        <span className="text-sm text-white">{s.staff_name}</span>
                        <span className="text-xs text-teal-300">{s.staff_id}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveStaff(s.staff_id)}
                          className="w-6 h-6 rounded-full bg-teal-600/60 hover:bg-red-600 flex items-center justify-center text-white text-xs"
                          data-testid={`mobile-fg-staff-remove-${s.staff_id}`}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Button
                onClick={handleStartSession}
                disabled={loading || staffList.length === 0}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-fg-staff-continue-btn"
              >
                Continue ({staffList.length})
              </Button>
            </div>
          </div>
        )}

        {/* Step: Bundle Scan */}
        {step === 'bundle' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-6 space-y-6">
              <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <Box className="w-10 h-10 text-blue-400" />
              </div>
              <div className="text-center">
                <h2 className="text-2xl font-bold mb-2">Scan IN_WIP Bundle</h2>
                <p className="text-gray-400">From completed SFG pick slip</p>
              </div>
              <Input
                ref={bundleInputRef}
                value={bundleCode}
                onChange={(e) => setBundleCode(e.target.value)}
                onKeyPress={(e) => handleKeyPress(e, handleBundleScan)}
                placeholder="Bundle Barcode"
                className="h-16 text-xl text-center bg-gray-700 border-gray-600 rounded-xl font-mono"
                data-testid="mobile-fg-bundle-input"
              />
              <Button
                onClick={handleBundleScan}
                disabled={loading || !bundleCode}
                className="w-full h-16 text-xl font-bold bg-blue-600 hover:bg-blue-700 rounded-xl"
              >
                {loading ? 'Validating...' : 'Validate Bundle'}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Progress View - Session Recovery */}
        {step === 'progress-view' && progressData && (
          <div className="flex-1 flex flex-col space-y-4">
            {/* Progress Summary */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm text-blue-200">Resuming Put Away</p>
                  <p className="text-lg font-bold">{progressData.bundle_code || bundleCode}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={refreshProgress}
                  disabled={loading}
                  className="text-white hover:bg-white/10"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span>{progressData.progress_summary.completed} / {progressData.progress_summary.total_items}</span>
                  <span>{progressData.progress_summary.percentage}%</span>
                </div>
                <Progress value={progressData.progress_summary.percentage} className="h-3 bg-blue-900" />
              </div>
              <div className="flex gap-3 text-sm">
                <Badge className="bg-green-500/20 text-green-300">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {progressData.progress_summary.completed} done
                </Badge>
                <Badge className="bg-orange-500/20 text-orange-300">
                  <Circle className="w-3 h-3 mr-1" />
                  {progressData.progress_summary.pending} pending
                </Badge>
              </div>
            </div>

            {/* Completed USNs */}
            <div className="bg-gray-800 rounded-2xl p-4 flex-1 overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <h3 className="font-bold text-green-400">Completed ({progressData.completed_usns.length})</h3>
              </div>
              <div className="overflow-y-auto space-y-2 max-h-32">
                {progressData.completed_usns.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-2">No USNs put away yet</p>
                ) : (
                  progressData.completed_usns.map((usn, idx) => (
                    <div key={idx} className="bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                      <div className="flex justify-between items-center">
                        <p className="font-mono text-sm text-green-300">{usn.usn_code}</p>
                        <Badge variant="outline" className="text-xs border-green-500/50 text-green-400">
                          {usn.bin_code || usn.actual_bin}
                        </Badge>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Pending USNs */}
            <div className="bg-gray-800 rounded-2xl p-4 flex-1 overflow-hidden">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-orange-400" />
                <h3 className="font-bold text-orange-400">Pending ({progressData.pending_usns.length})</h3>
              </div>
              <div className="overflow-y-auto space-y-2 max-h-32">
                {progressData.pending_usns.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-2">All USNs put away!</p>
                ) : (
                  progressData.pending_usns.map((usn, idx) => (
                    <div key={idx} className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2">
                      <div className="flex justify-between items-center">
                        <p className="font-mono text-sm text-orange-300">{usn.usn_code}</p>
                        {usn.suggested_bin && (
                          <Badge variant="outline" className="text-xs border-orange-500/50 text-orange-400">
                            → {usn.suggested_bin}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Continue Button */}
            <Button
              onClick={continueFromProgress}
              disabled={loading}
              className="w-full h-14 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-lg font-bold rounded-xl"
            >
              <Scan className="w-6 h-6 mr-2" />
              Continue Put Away
            </Button>
          </div>
        )}

        {/* Step: Pack Put-Away */}
        {step === 'pack' && bundleDetails && (
          <div className="flex-1 flex flex-col space-y-4">
            {/* Bundle Info */}
            <div className="bg-gray-800 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-400 text-sm">Bundle</span>
                <span className="font-mono font-bold">{bundleCode}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-700 rounded-xl p-2">
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="font-bold text-lg">{bundleDetails.total_packs}</p>
                </div>
                <div className="bg-green-500/20 rounded-xl p-2">
                  <p className="text-xs text-green-400">Done</p>
                  <p className="font-bold text-lg text-green-400">{bundleDetails.putaway_count}</p>
                </div>
                <div className="bg-blue-500/20 rounded-xl p-2">
                  <p className="text-xs text-blue-400">Left</p>
                  <p className="font-bold text-lg text-blue-400">{bundleDetails.remaining_packs}</p>
                </div>
              </div>
            </div>

            {/* Suggested Bin */}
            {suggestedBin && (
              <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-green-200">Suggested Bin</p>
                    <p className="text-2xl font-bold">{suggestedBin.bin_code}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-green-200">{suggestedBin.current_qty}/{suggestedBin.capacity}</p>
                    <p className="text-green-300">packs</p>
                  </div>
                </div>
              </div>
            )}

            {/* Pack & Bin Inputs */}
            <div className="bg-gray-800 rounded-2xl p-4 space-y-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Scan Pack Barcode</label>
                <Input
                  ref={packInputRef}
                  value={packBarcode}
                  onChange={(e) => setPackBarcode(e.target.value)}
                  placeholder="Pack Barcode"
                  className="h-14 text-lg bg-gray-700 border-gray-600 rounded-xl font-mono"
                  data-testid="mobile-fg-pack-input"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">Bin Location</label>
                <Input
                  ref={binInputRef}
                  value={binCode}
                  onChange={(e) => setBinCode(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, handlePackPutAway)}
                  placeholder="Bin Code"
                  className="h-14 text-lg bg-gray-700 border-gray-600 rounded-xl font-mono"
                  data-testid="mobile-fg-bin-input"
                />
              </div>
            </div>

            {/* Zone Rules */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
              <p className="text-sm text-yellow-400">
                <strong>Zone A/B:</strong> 1 SKU per bin | <strong>Zone C:</strong> Multi-SKU OK
              </p>
            </div>

            {/* Actions */}
            <div className="mt-auto space-y-3">
              <Button
                onClick={handlePackPutAway}
                disabled={loading || !packBarcode || !binCode}
                className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 rounded-xl"
                data-testid="mobile-fg-confirm-putaway"
              >
                {loading ? 'Processing...' : 'Put Away Pack'}
              </Button>
              <Button
                variant="outline"
                onClick={resetForNextBundle}
                className="w-full h-12 border-gray-600 text-gray-300 hover:bg-gray-800 rounded-xl"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Switch Bundle
              </Button>
            </div>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-gray-800 rounded-3xl p-8 text-center space-y-6">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle className="w-14 h-14 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-green-400">Bundle Complete!</h2>
                <p className="text-gray-400 mt-2">All packs stored successfully</p>
              </div>
              <p className="text-sm text-gray-500">Ready for next bundle...</p>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      {step !== 'staff' && step !== 'success' && (
        <footer className="bg-gray-800 px-4 py-3 safe-area-inset-bottom">
          <Button
            variant="ghost"
            onClick={resetAll}
            className="w-full text-gray-400 hover:text-white"
          >
            End Session
          </Button>
        </footer>
      )}
    </div>
  );
};

export default MobileFGPutAway;
